import { SeededRng } from './seeded-rng';

/**
 * A character's sigil: a woven knot, one continuous line that loops and
 * crosses itself, seeded from the character's id so it never changes and
 * every client draws the same one (see docs/gameplay/skill-tree-ui.md, Sigil).
 *
 * The knot is a projected torus knot: a closed curve that winds `turns` times
 * around the center while weaving in and out `weaves` times. Each sample
 * carries a depth (`z`), and at every crossing the two strands have opposite
 * depths, which is what a renderer uses to draw one strand over the other.
 */
export interface SigilKnotPoint {
    /** Position in a unit box: the knot fits within radius 1 of the origin. */
    readonly x: number;
    readonly y: number;
    /** Depth: positive strands pass over negative ones where they cross. */
    readonly z: number;
}

export interface SigilKnot {
    /** Samples around the closed curve, in order; the last connects back to the first. */
    readonly points: readonly SigilKnotPoint[];
    /** Times the strand winds around the center. */
    readonly turns: number;
    /** Times the strand weaves in and out per full circuit. */
    readonly weaves: number;
}

/** Coprime (turns, weaves) pairs that read as balanced Celtic-style knots. */
const KNOT_FORMS: readonly (readonly [number, number])[] = [
    [2, 3],
    [2, 5],
    [3, 4],
    [3, 5],
    [2, 7],
    [4, 5],
    [3, 7],
];

const SAMPLES_PER_WEAVE = 48;

export function buildSigilKnot(seed: string): SigilKnot {
    const rng = new SeededRng(`sigil:${seed}`);
    const [turns, weaves] = KNOT_FORMS[Math.floor(rng.random() * KNOT_FORMS.length)]!;
    // How deep the strand dips toward the center, and how the knot is turned.
    // (The radius must depend on cos(weaves·t) alone: that keeps the two strands
    // at every crossing at opposite depths, so the weave always alternates cleanly.)
    const depth = 0.42 + rng.random() * 0.2;
    const rotation = rng.random() * Math.PI * 2;

    const count = weaves * SAMPLES_PER_WEAVE;
    const raw = Array.from({ length: count }, (_, index) => {
        const t = (index / count) * Math.PI * 2;
        const radius = 1 - depth * (0.5 + 0.5 * Math.cos(weaves * t));
        const angle = turns * t + rotation;
        return { x: radius * Math.cos(angle), y: radius * Math.sin(angle), z: -Math.sin(weaves * t) };
    });

    // Normalize so the knot's outermost point sits exactly on the unit circle.
    const extent = Math.max(...raw.map((point) => Math.hypot(point.x, point.y)));
    return {
        points: raw.map((point) => ({ x: point.x / extent, y: point.y / extent, z: point.z })),
        turns,
        weaves,
    };
}
